import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { Chart } from 'angular-highcharts';
import { SeriesOptionsType, YAxisOptions } from 'highcharts';
import * as moment from 'moment';
import { RetrofitRawDataTestItem, RetrofitService } from '../../../services/retrofit.service';

interface RawData {
  g1: [number, number][];
  g2: [number, number][];
  g3: [number, number][];
}

@Component({
  selector: 'app-retrofit-raw-data',
  templateUrl: './retrofit-raw-data.component.html',
  styleUrls: ['./retrofit-raw-data.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class RetrofitRawDataComponent implements OnInit {
  constructor(private retrofitService: RetrofitService) {}

  chart: Chart;

  // all records fetched once; dates, tests and files are all derived from this locally
  allTestRecords: RetrofitRawDataTestItem[] = [];

  testslist: string[] = [];
  filesList: string[] = [];

  masterParametersList: string[] = [];
  redSearchKeyword: string = '';
  blueSearchKeyword: string = '';
  greenSearchKeyword: string = '';
  redLineParameter: string = '';
  blueLineParameter: string = '';
  greenLineParameter: string = '';

  // a param already picked for one line is excluded from the other two lists
  get redLineParameterList(): string[] {
    return this.filterParams(this.redSearchKeyword, [this.blueLineParameter, this.greenLineParameter]);
  }

  get blueLineParameterList(): string[] {
    return this.filterParams(this.blueSearchKeyword, [this.redLineParameter, this.greenLineParameter]);
  }

  get greenLineParameterList(): string[] {
    return this.filterParams(this.greenSearchKeyword, [this.redLineParameter, this.blueLineParameter]);
  }

  private filterParams(keyword: string, excludeValues: string[]): string[] {
    const lowerKeyword = keyword.toLowerCase();

    return this.masterParametersList.filter(
      (param) => param.toLowerCase().includes(lowerKeyword) && !excludeValues.includes(param)
    );
  }

  selectedTest: string = '';
  selectedFile: string = '';

  rawData: RawData = {
    g1: [],
    g2: [],
    g3: [],
  };
  selectedDate = new Date();
  datesToHighlight: string[] = [];
  isTestDatesReceived: boolean = false;
  isTestSelected: boolean = false;
  isFileSelected: boolean = false;
  isRawDataReceived: boolean = false;
  isFetchingRawData: boolean = false;
  noParamsAvailable: boolean = false;

  ngOnInit(): void {
    this.getRetrofitRawDataTests();
  }

  udpateParamterList(linetype: 'red' | 'blue' | 'green', event: Event) {
    const keyword = (event.target as HTMLInputElement).value.toLowerCase();

    if (linetype === 'red') {
      this.redSearchKeyword = keyword;
    } else if (linetype === 'blue') {
      this.blueSearchKeyword = keyword;
    } else {
      this.greenSearchKeyword = keyword;
    }
  }

  // fetches the full list of retrofit raw-data records once; dates, tests and
  // files for the currently selected date/test are all filtered from this locally
  getRetrofitRawDataTests() {
    this.datesToHighlight = [];
    this.isTestDatesReceived = false;

    this.retrofitService.getRetrofitRawDataTests().subscribe(
      (res) => {
        if (res.body.success) {
          this.allTestRecords = res.body.data;

          // dedupe dates - only the date portion matters, time is ignored
          const uniqueDates = new Set(
            this.allTestRecords.map((record) => moment(record.date).format('YYYY-MM-DD'))
          );
          this.datesToHighlight = Array.from(uniqueDates);
          this.isTestDatesReceived = true;

          this.updateTestsForDate(this.selectedDate);
        }
      },
      (error) => {
        console.log('error getting retrofit raw data tests: ', error);
      }
    );
  }

  updateTestsForDate(selectedDate: Date) {
    const recordsForDate = this.allTestRecords.filter((record) =>
      moment(record.date).isSame(selectedDate, 'day')
    );

    this.testslist = Array.from(new Set(recordsForDate.map((record) => record.test_id)));
  }

  updateFilesForTest() {
    this.filesList = [];
    this.selectedFile = '';
    this.isFileSelected = false;
    this.isRawDataReceived = false;
    this.isFetchingRawData = false;
    this.noParamsAvailable = false;
    this.resetParametersList();

    const recordsForTest = this.allTestRecords.filter(
      (record) =>
        record.test_id === this.selectedTest && moment(record.date).isSame(this.selectedDate, 'day')
    );

    this.filesList = Array.from(new Set(recordsForTest.map((record) => record.file_name))).sort(
      (a, b) => a.localeCompare(b)
    );
    this.isTestSelected = this.selectedTest.length > 0;
  }

  onFileSelected() {
    this.isFileSelected = this.selectedFile.length > 0;

    if (this.isFileSelected) {
      this.getRetrofitRawParams();
    }
  }

  getRetrofitRawData() {
    const payload: {
      testId: string;
      fileName: string;
      p1?: string;
      p2?: string;
      p3?: string;
    } = {
      testId: this.selectedTest,
      fileName: this.selectedFile,
    };

    if (this.redLineParameter) {
      payload.p1 = this.redLineParameter;
    }
    if (this.blueLineParameter) {
      payload.p2 = this.blueLineParameter;
    }
    if (this.greenLineParameter) {
      payload.p3 = this.greenLineParameter;
    }

    // matches backend validation - at least one param must be selected
    if (!payload.p1 && !payload.p2 && !payload.p3) {
      this.rawData = { g1: [], g2: [], g3: [] };
      this.isRawDataReceived = false;
      this.isFetchingRawData = false;
      return;
    }

    this.isRawDataReceived = false;
    this.isFetchingRawData = true;

    this.retrofitService.getRetrofitRawData(payload).subscribe(
      (res) => {
        this.isFetchingRawData = false;

        if (res.body.success) {
          const data = res.body.data;
          const toSeries = (paramName: string): [number, number][] =>
            paramName && data[paramName] ? data[paramName] : [];

          this.rawData = {
            g1: toSeries(this.redLineParameter),
            g2: toSeries(this.blueLineParameter),
            g3: toSeries(this.greenLineParameter),
          };
          this.isRawDataReceived = true;
          this.plotChart(this.rawData);
        }
      },
      (error) => {
        this.isFetchingRawData = false;
        console.log('error getting retrofit raw data: ', error);
      }
    );
  }

  plotChart(chartData: RawData) {
    const { g1, g2, g3 } = chartData;
    const series: SeriesOptionsType[] = [];
    const yAxis: YAxisOptions[] = Array(3).fill({
      title: {
        text: '',
      },
    });

    const config = {
      g1: {
        color: '#eb262d',
        title: this.redLineParameter,
      },
      g2: {
        color: '#34495e',
        title: this.blueLineParameter,
      },
      g3: {
        color: '#16a085',
        title: this.greenLineParameter,
      },
    };

    if (g1.length) {
      series.push({
        type: 'spline',
        color: config.g1.color,
        name: config.g1.title,
        data: chartData.g1,
        yAxis: 0,
      });

      yAxis[0] = {
        title: {
          text: config.g1.title,
          style: {
            color: config.g1.color,
            fontWeight: 'bold',
          },
        },
        labels: {
          style: {
            color: config.g1.color,
          },
          align: 'right',
        },
      };
    }

    if (g2.length) {
      series.push({
        type: 'spline',
        color: config.g2.color,
        name: config.g2.title,
        data: chartData.g2,
        yAxis: 1,
      });

      yAxis[1] = {
        title: {
          text: config.g2.title,
          style: {
            color: config.g2.color,
            fontWeight: 'bold',
          },
          x: -10,
        },
        opposite: true,
        labels: {
          style: {
            color: config.g2.color,
          },
          x: -10,
        },
      };
    }

    if (g3.length) {
      series.push({
        type: 'spline',
        color: config.g3.color,
        name: config.g3.title,
        data: chartData.g3,
        yAxis: 2,
      });

      yAxis[2] = {
        title: {
          text: config.g3.title,
          style: {
            color: config.g3.color,
            fontWeight: 'bold',
          },
          x: 10,
        },
        opposite: true,
        labels: {
          style: {
            color: config.g3.color,
          },
          align: 'right',
        },
      };
    }

    this.chart = new Chart({
      chart: {
        type: 'spline',
        zoomType: 'x',
        resetZoomButton: {
          position: {
            align: 'right',
            verticalAlign: 'top',
            x: -10,
            y: -50,
          },
        },
      },
      title: {
        text: 'Retrofit Raw Data',
        style: {
          top: '-5px',
        },
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      xAxis: [
        {
          type: 'datetime',
          crosshair: true,
          title: {
            text: 'Time',
            style: {
              color: '#34495e',
              fontWeight: 'bold',
            },
          },
        },
      ],
      yAxis,
      tooltip: {
        xDateFormat: '%e %b,%Y @ %H:%M:%S',
        shared: true,
      },
      time: {
        timezoneOffset: -330, // UTC -5:30 (IST)
      },
      series,
    });
  }

  highlightDates(): MatCalendarCellClassFunction<Date> {
    return (cellDate, view) => {
      // Only highlight dates inside the month view.
      if (view === 'month') {
        const date = cellDate.getDate();

        const validHighlight = this.datesToHighlight
          .map((stringDate: string) => new Date(stringDate))
          .some(
            (generatedDate: Date) =>
              generatedDate.getDate() === date &&
              generatedDate.getMonth() === cellDate.getMonth() &&
              generatedDate.getFullYear() === cellDate.getFullYear()
          );

        return validHighlight ? 'special-date' : '';
      }

      return '';
    };
  }

  dateChange(selectedDate: Date) {
    this.testslist = [];
    this.filesList = [];
    this.masterParametersList = [];
    this.selectedTest = '';
    this.selectedFile = '';
    this.isTestSelected = false;
    this.isFileSelected = false;
    this.isRawDataReceived = false;
    this.isFetchingRawData = false;
    this.noParamsAvailable = false;

    this.resetParametersList();
    this.updateTestsForDate(selectedDate);
  }

  getRetrofitRawParams() {
    this.masterParametersList = [];
    this.noParamsAvailable = false;
    this.isFetchingRawData = true;

    this.retrofitService
      .getRetrofitRawParams({
        testId: this.selectedTest,
        fileName: this.selectedFile,
      })
      .subscribe(
        (res) => {
          if (res.body.success) {
            this.masterParametersList = res.body.data;

            this.resetParametersList();

            if (this.masterParametersList.length === 0) {
              this.noParamsAvailable = true;
              this.isFetchingRawData = false;
              return;
            }

            this.redLineParameter = this.redLineParameterList[1];
            this.getRetrofitRawData();
          } else {
            this.noParamsAvailable = true;
            this.isFetchingRawData = false;
          }
        },
        (error) => {
          this.isFetchingRawData = false;
          console.log('error getting retrofit raw params: ', error);
        }
      );
  }

  resetParametersList() {
    this.redSearchKeyword = '';
    this.blueSearchKeyword = '';
    this.greenSearchKeyword = '';
    this.redLineParameter = '';
    this.blueLineParameter = '';
    this.greenLineParameter = '';
  }
}
